import React from 'react';
import { Navbar } from '../../components/Navbar';
import { useTranslation } from 'react-i18next';

import AboutUsBG from '../../assets/AboutUsBG.jpg';
import ProjectProcessTraditionalDiagramEN from '../../assets/project-process-traditional.svg';
import ProjectProcessTraditionalDiagramES from '../../assets/project-process-traditional-Spanish.svg';
import ProjectProcessSliDiagramEN from '../../assets/project-process-SLI.svg';
import ProjectProcessSliDiagramES from '../../assets/project-process-Spanish-SLI.svg';
import SLI_capacityEN from '../../assets/SLI_capacity.svg';
import SLI_capacityES from '../../assets/SLI_capacity-Spanish.svg';
import Aggregation from '../../assets/Aggregation.svg';
import { PictureContentRight } from '../../components/PictureContentRight';
import { PictureContentLeft } from '../../components/PictureContentLeft';
import { ContentBox } from '../../components/ContentBox';
import { ArrowRight } from 'lucide-react';
import { ContactUsButton } from '../../components/buttons/ContactUsButton';

const AboutFarmOsCpPage = () => {
  const { t, i18n } = useTranslation();

  // Mapping object for all localized images
  const images = {
    projectProcessTraditional: {
      en: ProjectProcessTraditionalDiagramEN,
      es: ProjectProcessTraditionalDiagramES,
    },
    projectProcessSli: {
      en: ProjectProcessSliDiagramEN,
      es: ProjectProcessSliDiagramES,
    },
    sliCapacity: {
      en: SLI_capacityEN,
      es: SLI_capacityES,
    },
  };

  // Helper function to get the correct image based on current language
  const getImage = (imageKey) => {
    return images[imageKey][i18n.language] || images[imageKey].en;
  };

  return (
    <div className="mx-auto flex min-h-screen w-full max-w-full flex-col bg-white">
      <Navbar
        src={AboutUsBG}
        pageName={t('aboutFarmOsCp.navbar.pageName')}
        pageText={t('aboutFarmOsCp.navbar.pageText')}
      />
      <ContentBox
        title={t('aboutFarmOsCp.sections.aboutFarmOsCp.title')}
        content={
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-SLIGreen mb-3">
                {t('aboutFarmOsCp.sections.aboutFarmOsCp.farmNativeData')}
              </h3>
              <p className="mb-4">{t('aboutFarmOsCp.sections.aboutFarmOsCp.paragraph3')}</p>
              <p>{t('aboutFarmOsCp.sections.aboutFarmOsCp.paragraph4')}</p>
            </div>
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-lg font-semibold text-SLIGreen mb-3">
                {t('aboutFarmOsCp.sections.aboutFarmOsCp.builtOnFarmOs')}
              </h3>
              <p className="mb-4">
                {t('aboutFarmOsCp.sections.aboutFarmOsCp.paragraph1Part1')}
                <a
                  href="https://farmos.org/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-SLIGreen hover:underline font-medium"
                >
                  {t('aboutFarmOsCp.sections.aboutFarmOsCp.farmOSLinkText')}
                </a>
                {t('aboutFarmOsCp.sections.aboutFarmOsCp.paragraph1Part2')}
              </p>
              <p>{t('aboutFarmOsCp.sections.aboutFarmOsCp.paragraph2')}</p>
            </div>
          </div>
        }
        bgColor={'#FFFFFF'}
        titleColor={'#2E7A32'}
        textAlign={'text-left'}
      />
      <ContentBox
        title={t('aboutFarmOsCp.sections.howToGet.title')}
        content={
          <div className="flex flex-col md:flex-row md:items-center gap-8">
            <div className="flex-1">
              <p className="mb-4">{t('aboutFarmOsCp.sections.howToGet.paragraph1')}</p>
              <p>{t('aboutFarmOsCp.sections.howToGet.paragraph2')}</p>
            </div>
            <div className="flex-shrink-0">
              <ContactUsButton>
                {t('aboutFarmOsCp.sections.howToGet.contactButton')}
              </ContactUsButton>
            </div>
          </div>
        }
        bgColor={'#FFFFFF'}
        titleColor={'#2E7A32'}
        textAlign={'text-left'}
      />
      <ContentBox
        title={t('aboutFarmOsCp.sections.howItWorks.title')}
        content={
          <div>
            {t('aboutFarmOsCp.sections.howItWorks.text1')}
            <img
              className={`w-[800px] mx-auto my-4`}
              src={getImage('projectProcessTraditional')}
              alt="The traditional process of project development for Conservation Districts."
            />
            <p>
              {t('aboutFarmOsCp.sections.howItWorks.text2Part1')}{' '}
              <strong>{t('aboutFarmOsCp.sections.howItWorks.text2Strong1')}</strong>{' '}
              {t('aboutFarmOsCp.sections.howItWorks.text2Part2')}{' '}
              <strong>{t('aboutFarmOsCp.sections.howItWorks.text2Strong2')}</strong>{' '}
              {t('aboutFarmOsCp.sections.howItWorks.text2Part3')}
            </p>
            <img
              className={`w-[800px] mx-auto my-4`}
              src={getImage('projectProcessSli')}
              alt="The traditional process of project development for Conservation Districts."
            />
            <p>
              {t('aboutFarmOsCp.sections.howItWorks.text3Part1')}{' '}
              <strong>{t('aboutFarmOsCp.sections.howItWorks.text3Strong1')}</strong>
              {t('aboutFarmOsCp.sections.howItWorks.text3Part2')}{' '}
              <strong>{t('aboutFarmOsCp.sections.howItWorks.text3Strong2')}</strong>
              {t('aboutFarmOsCp.sections.howItWorks.text3Part3')}
            </p>
            <p className="flex items-center gap-2 pt-5 text-m font-bold text-[#2E7A32]">
              <ArrowRight className="h-6 w-6" aria-hidden={true} />
              {t('aboutFarmOsCp.sections.howItWorks.efficiency')}
            </p>
          </div>
        }
        bgColor={'#FFFFFF'}
        titleColor={'#2E7A32'}
      />
      <PictureContentLeft
        bgColor="#FFFFFF"
        srcImg={Aggregation}
        titleColor={'#2E7A32'}
        title={t('aboutFarmOsCp.sections.equitableAccess.title')}
        content={
          <div>
            {t('aboutFarmOsCp.sections.equitableAccess.textPart1')}{' '}
            <strong>{t('aboutFarmOsCp.sections.equitableAccess.textStrong')}</strong>
            {t('aboutFarmOsCp.sections.equitableAccess.textPart2')}
          </div>
        }
        contentColor="#000000"
      />
      <PictureContentRight
        bgColor="#FFFFFF"
        srcImg={getImage('sliCapacity')}
        titleColor={'#2E7A32'}
        title={t('aboutFarmOsCp.sections.capacity.title')}
        content={
          <div>
            <p>{t('aboutFarmOsCp.sections.capacity.text1Part1')}</p>
            &nbsp;
            <p>{t('aboutFarmOsCp.sections.capacity.text2')}</p>
          </div>
        }
        contentColor="#000000"
      />
      <ContentBox
        title={t('aboutFarmOsCp.sections.strategy.title')}
        content={
          <div>
            {t('aboutFarmOsCp.sections.strategy.textPart1')}{' '}
            <em>{t('aboutFarmOsCp.sections.strategy.textItalic')}</em>
            {t('aboutFarmOsCp.sections.strategy.textPart2')}
          </div>
        }
        bgColor={'#FFFFFF'}
        titleColor={'#2E7A32'}
      />
      {/* ── TRADEMARK NOTICE ─────────────────────────── */}
      <ContentBox
        content={
          <p className="text-sm text-gray-500 leading-relaxed">
            {t('aboutFarmOsCp.sections.trademark.part1')}
            <a
              href="https://farmos.org/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-SLIGreen hover:underline"
            >
              {t('aboutFarmOsCp.sections.trademark.farmOSPlatform')}
            </a>
            {t('aboutFarmOsCp.sections.trademark.part2')}
            <a
              href="https://farmos.org/community/trademark/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-SLIGreen hover:underline"
            >
              {t('aboutFarmOsCp.sections.trademark.registeredTrademark')}
            </a>
            {t('aboutFarmOsCp.sections.trademark.part3')}
          </p>
        }
        bgColor={'#FFFFFF'}
        titleColor={'#2E7A32'}
      />
      {/*<PictureContentRight
          title="Resources"
          titleColor="#2E7A32"
          content={`Lorem ipsum dolor sit amet consectetur adipisicing elit. Voluptatum
             molestiae delectus culpa hic assumenda, voluptate reprehenderit
             dolore autem cum ullam sed odit perspiciatis. Doloribus quos velit,
             eveniet ex deserunt fuga?.
             Lorem ipsum dolor sit amet consectetur adipisicing elit. Voluptatum
             molestiae delectus culpa hic assumenda, voluptate reprehenderit
             dolore autem cum ullam sed odit perspiciatis. Doloribus quos velit,
             eveniet ex deserunt fuga?`}
          srcImg={PictureContent}
          btnContent="Find more"
          btnColor="#A8C356"
          display="inline-block"
        />*/}
    </div>
  );
};

export { AboutFarmOsCpPage };
